import React, { useState } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  quotePlugin,
  headingsPlugin,
  tablePlugin,
  thematicBreakPlugin,
  frontmatterPlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  markdownShortcutPlugin,
  BlockTypeSelect,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  Separator
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { CancelButton, PrimaryButton } from '../../../Components/Buttons';
import '../agent-training.css';
import { useLanguage } from '../../../Context/LanguageContext';

// Compact MDXEditor wrapper with smaller buttons
const CompactMDXEditor = ({ value, onChange, placeholder }) => {
  return (
    <div style={{
      border: '2px solid #f0f0f0',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#fff',
      transition: 'all 0.3s ease',
      minWidth: 0,
      flexShrink: 1,
    }}>
      <style>{`
        .mdxeditor {
          font-size: 14px;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          --mdxeditor-color: #2c1810;
          --mdxeditor-border: #f0f0f0;
          min-width: 0;
          max-width: 100%;
        }
        .mdxeditor-toolbar {
          border-bottom: 1px solid #f0f0f0;
          background-color: #fafbfc;
          padding: 8px 12px;
          min-height: 40px;
          display: flex;
          align-items: center;
          gap: 4px;
          min-width: 0;
          flex-wrap: wrap;
        }
        .mdxeditor-root-contenteditable {
          min-height: 300px;
          padding: 0;
          font-size: 16px;
          line-height: 1.8;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }
        .mdxeditor-toolbar button {
          border-radius: 6px;
          padding: 6px 10px;
          transition: all 0.2s ease;
          font-size: 13px;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mdxeditor-toolbar button:hover {
          background-color: rgba(85, 41, 127, 0.1);
          transform: translateY(-1px);
        }
        .mdxeditor-toolbar select {
          font-size: 13px;
          padding: 4px 8px;
          height: 32px;
          border: 1px solid #ddd;
          border-radius: 6px;
          background: white;
        }
        .mdxeditor-toolbar .mdxeditor-separator {
          height: 16px;
          margin: 0 2px;
        }
        .mdxeditor-toolbar label {
          position: absolute;
          left: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }
        .mdxeditor-toolbar button svg {
          width: 16px;
          height: 16px;
        }
        .mdxeditor-dialog {
          z-index: 9999;
        }
        .mdxeditor-popup-container {
          z-index: 9999;
        }
        @media (max-width: 768px) {
          .mdxeditor-toolbar {
            padding: 6px 8px;
          }
        }
      `}</style>
      <MDXEditor
        markdown={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        plugins={[
          headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
          listsPlugin(),
          quotePlugin(),
          tablePlugin(),
          thematicBreakPlugin(),
          codeBlockPlugin({
            defaultCodeBlockLanguage: 'text',
            codeBlockEditorDescriptors: []
          }),
          codeMirrorPlugin({
            codeBlockLanguages: {
              text: 'Plain text',
              javascript: 'JavaScript',
              typescript: 'TypeScript',
              css: 'CSS',
              html: 'HTML',
              python: 'Python',
              json: 'JSON',
              bash: 'Bash',
              sql: 'SQL'
            }
          }),
          frontmatterPlugin(),
          markdownShortcutPlugin(),
          toolbarPlugin({
            toolbarContents: () => (
              <>
                <UndoRedo />
                <Separator />
                <BoldItalicUnderlineToggles />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <ListsToggle />
                <Separator />
                <InsertTable />
                <InsertThematicBreak />
              </>
            )
          })
        ]}
      />
    </div>
  );
};

const AddTextModal = ({ show, onHide, workspaceId, apiUrl, onPollState }) => {
  const { t } = useLanguage();
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (!loading) {
      setTextTitle('');
      setTextContent('');
      setError(null);
      onHide();
    }
  };

  const handleAddText = async () => {
    try {
      setLoading(true);
      setError(null);
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Sanitize text content to fix any malformed emoji/surrogate pairs
      const sanitizeText = (text) => {
        try {
          // Fix unpaired surrogates by encoding and decoding properly
          // This will replace invalid surrogates with the replacement character
          const encoder = new TextEncoder();
          const decoder = new TextDecoder('utf-8', { fatal: false });
          const bytes = encoder.encode(text);
          return decoder.decode(bytes);
        } catch (e) {
          // Fallback: remove all unpaired surrogates
          return text.replace(/[\uD800-\uDFFF]/g, '');
        }
      };

      const response = await fetch(`${apiUrl}/api/v1/train/add-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          text_title: sanitizeText(textTitle),
          text: sanitizeText(textContent),
        }),
      });

      if (!response.ok)
        throw new Error(t('dashboard.training.modals.addtextmodal.errorFailed'));

      handleClose();
      await onPollState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isTextTooLong = textContent.length > 100000;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      centered
      size="lg"
      className="training-display-container"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{t('dashboard.training.modals.addtextmodal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(error || isTextTooLong) && (
          <Alert
            variant='danger'
            onClose={() => setError(null)}
            dismissible={!isTextTooLong}
          >
            {error || t('dashboard.training.modals.addtextmodal.errorTooLong')}
          </Alert>
        )}
        <Form>
          <Form.Group className='mb-4'>
            <Form.Label>
              {t('dashboard.training.modals.addtextmodal.titleLabel')}
            </Form.Label>
            <Form.Control
              type='text'
              value={textTitle}
              placeholder={t('dashboard.training.modals.addtextmodal.titlePlaceholder')}
              onChange={(e) => setTextTitle(e.target.value)}
              disabled={loading}
              maxLength={30}
            />
            <Form.Text>
              {t('dashboard.training.modals.addtextmodal.titleCharCount', { count: textTitle.length })}
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {t('dashboard.training.modals.addtextmodal.contentLabel')}
            </Form.Label>
            <CompactMDXEditor
              value={textContent}
              onChange={setTextContent}
              placeholder={t('dashboard.training.modals.addtextmodal.contentPlaceholder')}
            />
            <Form.Text className={isTextTooLong ? 'text-danger' : ''}>
              {t('dashboard.training.modals.addtextmodal.contentCharCount', { count: textContent.length })}
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton className='cancel-btn' onClick={handleClose} disabled={loading}>
          {t('dashboard.training.modals.addtextmodal.cancelButton')}
        </CancelButton>
        <PrimaryButton
          className='primary-btn'
          onClick={handleAddText}
          disabled={loading || !textTitle || !textContent || isTextTooLong}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              {t('dashboard.training.modals.addtextmodal.addingButton')}
            </>
          ) : (
            t('dashboard.training.modals.addtextmodal.addButton')
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTextModal;
